import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Copy, Clock, ExternalLink, CheckCircle2, ArrowRight, Bitcoin, Handshake, MessageCircle } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { ordersAPI, paymentAgentsAPI, fmtError } from '@/lib/api';
import { formatMoney, formatInt } from '@/lib/format';

export default function Checkout() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState('crypto');   // 'crypto' | 'agents'
  const [agents, setAgents] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    let stop = false;
    let timer = null;
    const load = async () => {
      try {
        const o = await ordersAPI.get(orderId);
        if (stop) return;
        setOrder(o);
        if (o.status === 'paid') { return; }
      } catch (e) { setError(fmtError(e)); }
      timer = setTimeout(load, 6000);
    };
    load();
    return () => { stop = true; if (timer) clearTimeout(timer); };
  }, [orderId]);

  useEffect(() => {
    if (tab === 'agents' && agents.length === 0) {
      paymentAgentsAPI.list().then(setAgents).catch(() => {});
    }
  }, [tab, agents.length]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(order.np_pay_address || '');
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  if (error) return <Shell><div className="text-[#ff3366]">{error}</div></Shell>;
  if (!order) return <Shell><div className="text-zinc-400 text-sm font-mono">LOADING ORDER…</div></Shell>;

  const isPaid = order.status === 'paid';
  const isFailed = order.status === 'failed';

  return (
    <Shell>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-tech rounded-lg p-8">
          <div className="label-sm">Evaluation Program Access · {order.order_id}</div>
          <h1 className="font-display text-3xl mt-2">
            {isPaid ? 'Payment confirmed' : isFailed ? 'Payment failed' : 'Awaiting payment'}
          </h1>

          {!isPaid && !isFailed && (
            <div className="mt-6 flex gap-2" data-testid="checkout-tabs">
              <button
                className={`px-4 py-2 rounded-md text-sm font-mono tracking-wider border transition ${tab === 'crypto' ? 'border-[#00ff66] bg-[#00ff66]/10 text-[#00ff66]' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}
                onClick={() => setTab('crypto')}
                data-testid="tab-crypto"
              >
                <Bitcoin size={14} className="inline mr-1" /> Pay with crypto
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-mono tracking-wider border transition ${tab === 'agents' ? 'border-[#00ff66] bg-[#00ff66]/10 text-[#00ff66]' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}
                onClick={() => setTab('agents')}
                data-testid="tab-agents"
              >
                <Handshake size={14} className="inline mr-1" /> Use a payment agent
              </button>
            </div>
          )}

          {!isPaid && !isFailed && tab === 'crypto' && (
            <div className="mt-6 space-y-5">
              <div className="flex items-center gap-2 chip chip-green w-max"><span className="pulse-dot" /> LIVE · {order.np_status || 'WAITING'}</div>
              <p className="text-zinc-400 text-sm max-w-md">
                Send exactly the amount below to the address shown. Your simulated evaluation account will be activated
                automatically once the transaction is confirmed.
              </p>

              <div className="card-tech rounded-lg p-5">
                <div className="label-xs">PAY AMOUNT</div>
                <div className="font-mono text-3xl mt-1">{order.np_pay_amount || order.amount_usd} <span className="text-zinc-500 text-base">{order.pay_currency?.toUpperCase() || 'USDT'}</span></div>
                <div className="text-xs text-zinc-500 mt-1">${formatMoney(order.amount_usd)} USD · {order.plan_name} · ${formatInt(order.account_size)} simulated account</div>
              </div>

              {order.np_pay_address && (
                <div className="card-tech rounded-lg p-5">
                  <div className="label-xs">PAY TO ADDRESS</div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="font-mono text-sm break-all flex-1" data-testid="pay-address">{order.np_pay_address}</div>
                    <button onClick={copy} className="btn-ghost btn-sm" data-testid="copy-address">
                      <Copy size={12} className="inline mr-1" />{copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              {order.np_invoice_url && (
                <a href={order.np_invoice_url} target="_blank" rel="noreferrer" className="btn-primary w-max" data-testid="open-invoice">
                  Open hosted checkout <ExternalLink size={14} />
                </a>
              )}

              <div className="flex items-center gap-2 text-xs text-zinc-500 mt-4">
                <Clock size={12} /> We auto-refresh every 6 seconds. You can safely close this page — we'll email you on confirmation.
              </div>
            </div>
          )}

          {!isPaid && !isFailed && tab === 'agents' && (
            <div className="mt-6 space-y-5" data-testid="agents-tab-content">
              <p className="text-zinc-400 text-sm max-w-md">
                Prefer paying through a local agent? Contact one of our approved Payment Agents below.
                Send them <span className="text-white font-mono">${formatMoney(order.amount_usd)} USD</span> via their preferred method, then forward the proof of payment.
                Once admin verifies, your account is activated.
              </p>
              {agents.length === 0 ? (
                <div className="card-tech rounded-lg p-6 text-sm text-zinc-400">
                  We're onboarding agents right now — please use crypto checkout above for instant access, or come back shortly.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="agents-list">
                  {agents.map(a => (
                    <div key={a.agent_id} className="card-tech rounded-lg p-4" data-testid={`co-agent-${a.agent_id}`}>
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-[#00ff66]/10 border border-[#00ff66]/40 flex items-center justify-center font-display text-sm text-[#00ff66] shrink-0">{a.name?.[0]?.toUpperCase() || 'A'}</div>
                        <div className="min-w-0">
                          <div className="text-white text-sm truncate">{a.name}</div>
                          {a.country && <div className="text-[11px] text-zinc-500">{a.country}</div>}
                        </div>
                      </div>
                      {(a.methods || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {a.methods.slice(0, 3).map(m => (
                            <span key={m} className="text-[10px] tracking-widest font-mono text-zinc-300 px-2 py-0.5 rounded-full bg-white/5 border border-white/10">{m}</span>
                          ))}
                        </div>
                      )}
                      <a href={a.contact_url} target="_blank" rel="noreferrer" className="btn-primary btn-sm w-full justify-center mt-3" data-testid={`co-contact-${a.agent_id}`}>
                        <MessageCircle size={12} /> Message agent
                      </a>
                    </div>
                  ))}
                </div>
              )}
              <Link to="/payment-agents" className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1">
                Browse all agents <ArrowRight size={12} />
              </Link>
              <div className="text-[11px] text-zinc-500 leading-relaxed border-t border-white/5 pt-3">
                Traders Spring Group is not affiliated with any payment agents. Agents are independent merchants — verify their identity and use due caution. Always include your order ID <span className="font-mono text-zinc-300">{order.order_id}</span> in your message.
              </div>
            </div>
          )}

          {isPaid && (
            <div className="mt-6">
              <div className="chip chip-green w-max mb-4"><CheckCircle2 size={12} /> CONFIRMED</div>
              <p className="text-zinc-400 text-sm max-w-md">Your simulated evaluation account has been activated. Credentials will arrive by email shortly.</p>
              <Link to="/dashboard" className="btn-primary mt-6" data-testid="go-dashboard">Go to dashboard <ArrowRight size={16} /></Link>
            </div>
          )}

          {isFailed && (
            <div className="mt-6">
              <div className="chip chip-red w-max mb-4">FAILED</div>
              <p className="text-zinc-400 text-sm max-w-md">This order expired or failed. You can start a new evaluation.</p>
              <button onClick={() => nav('/plans')} className="btn-primary mt-6">Start new evaluation</button>
            </div>
          )}
        </div>
        <aside className="card-tech rounded-lg p-6 h-fit sticky top-24">
          <div className="label-sm">Summary</div>
          <Row k="Program" v={order.plan_name} />
          <Row k="Size" v={`$${formatInt(order.account_size)}`} />
          <Row k="Evaluation fee" v={`$${formatMoney(order.amount_usd)}`} highlight />
          <Row k="Currency" v={order.pay_currency?.toUpperCase() || 'USDT'} />
          <Row k="Status" v={(order.status || '').toUpperCase()} />
          <div className="my-4 h-px bg-white/5" />
          <Link to="/dashboard" className="btn-ghost btn-sm w-full block text-center" data-testid="dashboard-link">Back to dashboard</Link>
        </aside>
      </div>
    </Shell>
  );
}

function Row({ k, v, highlight }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-zinc-400">{k}</span>
      <span className={`font-mono ${highlight ? 'text-[#00ff66] text-lg' : 'text-white text-sm'}`}>{v}</span>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-14">{children}</main>
      <Footer />
    </div>
  );
}
